import { useState, useEffect, useRef, useMemo, useDeferredValue, useCallback } from 'react';
import { X, Euro, Tag, Calendar, Save, Store, Search, Wallet, Calculator, User, Loader2 } from 'lucide-react';
import CustomSelect from './CustomSelect';
import { usePopup } from '../contexts/PopupContext';
import { getCurrentLocalDate, getCurrentLocalTime } from '../utils/formatters';
import { extractMoneyDigits, formatMoneyInput, amountToMoneyDigits, moneyDigitsToNumber } from '../utils/moneyInput';
import { isFutureDate } from '../features/expenses/expenseLogic';
import CalculatorModal from './CalculatorModal';
import { DEBT_WALLET_ID, CREDIT_WALLET_ID } from '../constants/debtWallets';
import { FREQUENCY_OPTIONS } from '../features/recurring/recurringLogic';
import MobileAmountKeypad from './MobileAmountKeypad';
import useIsMobileViewport from '../hooks/useIsMobileViewport';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const STORE_SUGGESTION_LIMIT = 12;

function ExpenseForm({ isOpen = true, onSubmit, onClose, type, editingItem = null, modelFormData = null, stores = [], categories = [], wallets = [], selectedWalletId, onAddStore, isModelMode = false, existingPersons = [], recurringOptions = null }) {
  const { showError, showConfirm } = usePopup();
  const getDefaultDebtType = useCallback(() => (type === 'income' ? 'credit' : 'debt'), [type]);
  const [formData, setFormData] = useState({
    amount: '',
    category: categories.length > 0 ? categories[0].name : '',
    date: getCurrentLocalDate(),
    time: getCurrentLocalTime(),
    store: '',
    walletId: selectedWalletId || (wallets[0]?.id ?? ''),
    note: '',
    debtPersonName: '',
    debtType: getDefaultDebtType(),
    isDebtTransaction: false
  });

  const deferredStoreQuery = useDeferredValue(formData.store);
  const sortedStores = useMemo(
    () => [...stores].sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' })),
    [stores]
  );
  const normalizedStores = useMemo(
    () => sortedStores.map((name) => ({ name, lower: name.toLowerCase() })),
    [sortedStores]
  );

  const [storeSuggestions, setStoreSuggestions] = useState([]);
  const [showStoreSuggestions, setShowStoreSuggestions] = useState(false);
  const [isStoreInputFocused, setIsStoreInputFocused] = useState(false);
  const [personSuggestions, setPersonSuggestions] = useState([]);
  const [showPersonSuggestions, setShowPersonSuggestions] = useState(false);
  const storeInputRef = useRef(null);
  const storeSuggestionsRef = useRef(null);
  const personInputRef = useRef(null);
  const personSuggestionsRef = useRef(null);
  const [destinationType, setDestinationType] = useState('wallet');
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMobileViewport = useIsMobileViewport();
  const [showMobileKeypad, setShowMobileKeypad] = useState(false);

  const isRecurringMode = Boolean(recurringOptions);
  const recurringFrequencyOptions = recurringOptions?.options || FREQUENCY_OPTIONS;
  const recurringFrequencyValue = recurringOptions?.frequency ?? recurringFrequencyOptions?.[0]?.value ?? 'monthly';
  const handleFrequencySelect = (value) => {
    recurringOptions?.onFrequencyChange?.(value);
  };

  const handleDateShift = (days) => {
    setFormData(prev => {
      if (!prev.date) return prev;
      const [year, month, day] = prev.date.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      date.setDate(date.getDate() + days);
      
      const newYear = date.getFullYear();
      const newMonth = String(date.getMonth() + 1).padStart(2, '0');
      const newDay = String(date.getDate()).padStart(2, '0');
      
      return {
        ...prev,
        date: `${newYear}-${newMonth}-${newDay}`
      };
    });
  };

  const extractStoredDateParts = (storedValue) => {
    if (!storedValue) {
      return { date: null, time: null };
    }

    const parsedDate = new Date(storedValue);

    if (!Number.isNaN(parsedDate?.getTime?.())) {
      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
      const day = String(parsedDate.getDate()).padStart(2, '0');
      const hours = String(parsedDate.getHours()).padStart(2, '0');
      const minutes = String(parsedDate.getMinutes()).padStart(2, '0');
      return {
        date: `${year}-${month}-${day}`,
        time: `${hours}:${minutes}`
      };
    }

    if (typeof storedValue === 'string') {
      const [datePart, timePartRaw] = storedValue.split('T');
      const timePart = timePartRaw ? timePartRaw.slice(0, 5) : null;
      return { date: datePart || null, time: timePart };
    }

    return { date: null, time: null };
  };

  // Inizializza il form con i dati dell'item da modificare o del modello
  // Gestione inizializzazione form e reset su cambio modalità
  useEffect(() => {
    const defaultCategory = (categories.length > 0 ? categories[0].name : '');
    const defaultWallet = selectedWalletId || (wallets[0]?.id ?? '');
    const defaultDebtType = getDefaultDebtType();

    if (modelFormData) {
      // Dati da un modello (nuova transazione)
      const modelIsDebt = Boolean(modelFormData.isDebtTransaction);
      const inferredDebtType = modelFormData.debtType || defaultDebtType;

      setDestinationType(modelIsDebt ? 'debt' : 'wallet');
      setFormData({
        amount: amountToMoneyDigits(modelFormData.amount),
        category: modelFormData.category || defaultCategory,
        date: getCurrentLocalDate(),
        time: getCurrentLocalTime(),
        store: modelFormData.store || '',
        walletId: modelIsDebt
          ? (inferredDebtType === 'credit' ? CREDIT_WALLET_ID : DEBT_WALLET_ID)
          : (modelFormData.walletId || defaultWallet),
        note: modelFormData.note || '',
        debtPersonName: modelFormData.debtPersonName || '',
        debtType: inferredDebtType,
        isDebtTransaction: modelIsDebt
      });
    } else if (editingItem) {
      // Dati da una transazione esistente (modifica)
      const { date: storedDate, time: storedTime } = extractStoredDateParts(editingItem.date);
      const editingIsDebt = Boolean(
        editingItem.isDebtTransaction ||
        editingItem.linkedDebtId ||
        editingItem.walletId === DEBT_WALLET_ID ||
        editingItem.walletId === CREDIT_WALLET_ID
      );
      const inferredDebtType = editingItem.debtType ||
        (editingItem.walletId === CREDIT_WALLET_ID ? 'credit' : 'debt');

      setDestinationType(editingIsDebt ? 'debt' : 'wallet');
      setFormData({
        amount: amountToMoneyDigits(editingItem.amount),
        category: editingItem.category || defaultCategory,
        date: storedDate || getCurrentLocalDate(),
        time: storedTime || getCurrentLocalTime(),
        store: editingItem.store || '',
        walletId: editingIsDebt
          ? (inferredDebtType === 'credit' ? CREDIT_WALLET_ID : DEBT_WALLET_ID)
          : (editingItem.walletId || defaultWallet),
        note: editingItem.note || '',
        debtPersonName: editingItem.debtPersonName || '',
        debtType: inferredDebtType,
        isDebtTransaction: editingIsDebt
      });
    } else {
      // Reset del form per nuovo inserimento
      // Manteniamo i valori correnti di category e walletId se validi, altrimenti usiamo i default
      setDestinationType('wallet');
      setFormData(prev => ({
        amount: '',
        category: isModelMode ? '' : (prev.category || defaultCategory),
        date: getCurrentLocalDate(),
        time: getCurrentLocalTime(),
        store: '',
        walletId: isModelMode ? '' : (prev.walletId || defaultWallet),
        note: '',
        debtPersonName: '',
        debtType: defaultDebtType,
        isDebtTransaction: false
      }));
    }
    // Rimuoviamo categories e lower inputs dalle dipendenze per evitare reset durante la digitazione
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem, modelFormData, isModelMode, type]);

  // Effect separato per aggiornare i default se le liste (categorie/wallet) vengono caricate dopo
  useEffect(() => {
    if (!editingItem && !modelFormData && !isModelMode) {
      setFormData(prev => {
        const needsCategoryUpdate = !prev.category && categories.length > 0;
        const needsWalletUpdate = !prev.walletId && wallets.length > 0;
        const needsSelectedWalletUpdate = selectedWalletId && !prev.walletId;

        if (!needsCategoryUpdate && !needsWalletUpdate && !needsSelectedWalletUpdate) return prev;

        return {
          ...prev,
          category: needsCategoryUpdate ? categories[0].name : prev.category,
          walletId: needsSelectedWalletUpdate
            ? selectedWalletId
            : (needsWalletUpdate ? wallets[0].id : prev.walletId)
        };
      });
    }
  }, [categories, wallets, selectedWalletId, editingItem, modelFormData, isModelMode]);

  // Gestisce il click fuori dai suggerimenti per chiuderli
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (storeSuggestionsRef.current &&
        !storeSuggestionsRef.current.contains(event.target) &&
        storeInputRef.current &&
        !storeInputRef.current.contains(event.target)) {
        setShowStoreSuggestions(false);
        setIsStoreInputFocused(false);
      }

      if (personSuggestionsRef.current &&
        !personSuggestionsRef.current.contains(event.target) &&
        personInputRef.current &&
        !personInputRef.current.contains(event.target)) {
        setShowPersonSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Disabilita l'autocompletamento del browser all'inizializzazione
  useEffect(() => {
    if (storeInputRef.current) {
      storeInputRef.current.setAttribute('autocomplete', 'off');
      storeInputRef.current.setAttribute('data-autocomplete', 'off');
    }
  }, []);

  useEffect(() => {
    if (!isStoreInputFocused) {
      setShowStoreSuggestions(false);
      return;
    }

    const query = (deferredStoreQuery || '').trim().toLowerCase();

    if (!query) {
      const topSuggestions = normalizedStores
        .slice(0, STORE_SUGGESTION_LIMIT)
        .map((item) => item.name);
      setStoreSuggestions(topSuggestions);
      setShowStoreSuggestions(topSuggestions.length > 0);
      return;
    }

    const startsWith = [];
    const contains = [];

    for (const item of normalizedStores) {
      if (item.lower.startsWith(query)) {
        startsWith.push(item.name);
      } else if (item.lower.includes(query)) {
        contains.push(item.name);
      }
      if (startsWith.length + contains.length >= STORE_SUGGESTION_LIMIT) {
        break;
      }
    }

    const nextSuggestions = [
      ...startsWith,
      ...contains.slice(0, Math.max(0, STORE_SUGGESTION_LIMIT - startsWith.length))
    ];

    setStoreSuggestions(nextSuggestions);
    setShowStoreSuggestions(nextSuggestions.length > 0);
  }, [deferredStoreQuery, normalizedStores, isStoreInputFocused]);

  // Aggiunge il negozio alla lista se non esiste
  const addStoreToSuggestions = async (storeName) => {
    if (storeName.trim() && !stores.includes(storeName.trim()) && onAddStore) {
      await onAddStore(storeName.trim());
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isModelMode) {
      // Validazione per modelli - solo il nome del modello è obbligatorio
      if (!formData.note || formData.note.trim() === '') {
        showError('Inserisci il nome del modello.', 'Campo obbligatorio');
        return;
      }

      setIsSubmitting(true);
      try {
        await onSubmit({
          ...formData,
          amount: moneyDigitsToNumber(formData.amount),
          store: formData.store.trim(),
          category: formData.category,
          name: formData.note.trim(), // Usa il campo note come nome del modello
          note: formData.note.trim()
        });
      } catch (error) {
        console.error('Error submitting model:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Validazione per transazioni normali
      if (!formData.amount) {
        if (!formData.amount) {
          showError('Inserisci un importo valido.', 'Campo obbligatorio');
        }
        return;
      }

      if (destinationType === 'wallet' && !formData.walletId) {
        showError('Seleziona un conto valido.', 'Campo obbligatorio');
        return;
      }

      if (destinationType === 'debt' && !formData.debtPersonName.trim()) {
        showError('Inserisci il nome della persona per il debito/credito.', 'Campo obbligatorio');
        return;
      }

      const fallbackStore = type === 'income' ? 'Entrata' : 'Spesa';
      const normalizedStore = formData.store.trim() || fallbackStore;

      // Controlla se la data è nel futuro (incluse transazioni di oggi con orario futuro)
      const dateTime = `${formData.date}T${formData.time}:00`;
      const isFutureTransaction = isFutureDate(dateTime);

      // Se è una transazione futura, chiedi conferma
      if (isFutureTransaction) {
        // Usa un Promise per gestire la conferma in modo asincrono
        const confirmed = await new Promise((resolve) => {
          showConfirm(
            <div>
              <p className="mb-3">Stai creando una transazione per un momento futuro.</p>
              <p className="mb-3">Questa transazione sarà visibile solo nella sezione "Transazioni Future" e verrà automaticamente spostata nelle transazioni normali quando arriverà il momento.</p>
              <p className="font-medium">Vuoi procedere?</p>
            </div>,
            'Transazione Futura',
            () => resolve(true),  // Conferma
            () => resolve(false)  // Annulla
          );
        });

        if (!confirmed) {
          return;
        }
      }

      // Aggiunge il negozio alla lista se non esiste
      await addStoreToSuggestions(normalizedStore);

      const isDebtDestination = destinationType === 'debt';
      const resolvedDebtType = formData.debtType || getDefaultDebtType();
      const resolvedWalletId = isDebtDestination
        ? (resolvedDebtType === 'credit' ? CREDIT_WALLET_ID : DEBT_WALLET_ID)
        : formData.walletId;

      const submissionData = {
        ...formData,
        amount: moneyDigitsToNumber(formData.amount),
        store: normalizedStore,
        date: dateTime,
        walletId: resolvedWalletId,
        isFutureTransaction: isFutureTransaction,
        isDebtTransaction: isDebtDestination
      };

      if (isDebtDestination) {
        submissionData.debtPersonName = formData.debtPersonName.trim();
        submissionData.debtType = resolvedDebtType;
      } else {
        delete submissionData.debtPersonName;
        delete submissionData.debtType;
      }

      setIsSubmitting(true);
      try {
        await onSubmit(submissionData);
      } catch (error) {
        console.error('Error submitting transaction:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'amount') {
      const digits = extractMoneyDigits(value);
      setFormData(prev => ({
        ...prev,
        amount: digits
      }));
      return;
    }

    if (name === 'store') {
      setFormData(prev => ({
        ...prev,
        store: value
      }));
      setShowStoreSuggestions(true);
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'debtPersonName') {
      updatePersonSuggestions(value);
    }
  };

  const handleStoreSelect = (store) => {
    setFormData(prev => ({ ...prev, store }));
    setShowStoreSuggestions(false);
    setIsStoreInputFocused(false);
    // Focus back to input after selection
    setTimeout(() => {
      if (storeInputRef.current) {
        storeInputRef.current.focus();
        setIsStoreInputFocused(true);
      }
    }, 100);
  };

  const handleStoreInputFocus = () => {
    // Disabilita completamente l'autocompletamento del browser
    if (storeInputRef.current) {
      storeInputRef.current.setAttribute('autocomplete', 'off');
      storeInputRef.current.setAttribute('data-autocomplete', 'off');
    }
    setIsStoreInputFocused(true);
    setShowStoreSuggestions(true);
  };

  const handleStoreInputBlur = () => {
    setIsStoreInputFocused(false);
    // Delay hiding suggestions to allow for touch events
    setTimeout(() => {
      setShowStoreSuggestions(false);
    }, 300);
  };

  const handleStoreInputKeyDown = (e) => {
    if (e.key === 'Enter' && storeSuggestions.length > 0) {
      e.preventDefault();
      handleStoreSelect(storeSuggestions[0]);
    } else if (e.key === 'Escape') {
      setShowStoreSuggestions(false);
    }
  };

  const updatePersonSuggestions = (value) => {
    const inputValue = value || '';
    const trimmed = inputValue.trim();

    if (!trimmed) {
      const initialSuggestions = existingPersons.slice(0, 5);
      setPersonSuggestions(initialSuggestions);
      setShowPersonSuggestions(
        initialSuggestions.length > 0 && document.activeElement === personInputRef.current
      );
      return;
    }

    const normalized = trimmed.toLowerCase();
    const filtered = existingPersons
      .filter(person => person.toLowerCase().includes(normalized))
      .sort((a, b) => {
        const aStarts = a.toLowerCase().startsWith(normalized);
        const bStarts = b.toLowerCase().startsWith(normalized);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        return a.localeCompare(b, 'it', { sensitivity: 'base' });
      })
      .slice(0, 5);

    setPersonSuggestions(filtered);
    setShowPersonSuggestions(filtered.length > 0);
  };

  const handlePersonSuggestionSelect = (person) => {
    setFormData(prev => ({ ...prev, debtPersonName: person }));
    setShowPersonSuggestions(false);
  };

  const handlePersonInputFocus = () => {
    if (personInputRef.current) {
      personInputRef.current.setAttribute('autocomplete', 'off');
      personInputRef.current.setAttribute('data-autocomplete', 'off');
    }
    updatePersonSuggestions(formData.debtPersonName);
  };

  const handlePersonInputBlur = () => {
    setTimeout(() => {
      setShowPersonSuggestions(false);
    }, 200);
  };

  const handleAmountInputFocus = (event) => {
    if (!isMobileViewport) {
      return;
    }
    event?.preventDefault?.();
    setShowMobileKeypad(true);
    event?.target?.blur?.();
  };

  const handleCalculatorResult = (value) => {
    setFormData(prev => ({
      ...prev,
      amount: amountToMoneyDigits(value)
    }));
    setIsCalculatorOpen(false);
  };

  const handleDestinationChange = (value) => {
    // Allow destination change in model mode to support debt/credit models
/*
    if (isModelMode && value === 'debt') {
      return;
    }
*/

    if (value === 'debt') {
      setDestinationType('debt');
      setFormData(prev => {
        const nextDebtType = prev.debtType || getDefaultDebtType();
        return {
          ...prev,
          debtType: nextDebtType,
          walletId: nextDebtType === 'credit' ? CREDIT_WALLET_ID : DEBT_WALLET_ID,
          isDebtTransaction: true
        };
      });
      updatePersonSuggestions(formData.debtPersonName);
    } else {
      setDestinationType('wallet');
      setFormData(prev => ({
        ...prev,
        walletId: selectedWalletId || (wallets[0]?.id ?? ''),
        isDebtTransaction: false
      }));
      setShowPersonSuggestions(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="bg-blue-600/90 backdrop-blur-sm text-white p-4 rounded-t-3xl">
            <div className="flex items-center justify-between">
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              <h2 className="text-xl font-bold">
                {isModelMode
                  ? `Nuovo Modello ${type === 'expense' ? 'Spesa' : 'Entrata'}`
                  : modelFormData
                    ? `Nuova ${type === 'expense' ? 'Spesa' : 'Entrata'}`
                    : `${editingItem ? 'Modifica' : 'Nuovo'} ${type === 'expense' ? 'Spesa' : 'Entrata'}`
                }
              </h2>
              <div className="w-6"></div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Riga 1: Importo + Categoria (+ Frequenza per ricorrenti) */}
            <div className="grid grid-cols-2 gap-4">
              <div className="mx-auto w-full max-w-[240px]">
                <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Importo
                </label>
                <div className="form-input-group">
                  <Euro className="form-input-icon" />
                  <input
                    type="text"
                    inputMode={isMobileViewport ? 'none' : 'decimal'}
                    name="amount"
                    value={formatMoneyInput(formData.amount)}
                    onChange={handleChange}
                    readOnly={isMobileViewport}
                    onFocus={handleAmountInputFocus}
                    onClick={handleAmountInputFocus}
                    onTouchEnd={handleAmountInputFocus}
                    placeholder="0,00"
                    autoComplete="off"
                    className="input form-input-with-icon"
                    required={!isModelMode}
                  />
                </div>

              </div>

              <div className="mx-auto w-full max-w-[240px]">
                <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                  <Tag className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  Categoria
                </label>
                <CustomSelect
                  value={formData.category}
                  onChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                  options={[
                    ...(isModelMode ? [{
                      value: '',
                      label: 'Nessuna',
                      icon: <div className="w-4 h-4 rounded-full border-2 border-gray-300 bg-gray-100" />
                    }] : []),
                    ...categories.map(category => ({
                      value: category.name,
                      label: category.name,
                      icon: category.icon
                    }))
                  ]}
                  placeholder="Seleziona categoria"
                  className="h-12"
                  required={!isModelMode}
                />
              </div>
            </div>

            {isRecurringMode && (
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Frequenza
                </label>
                <CustomSelect
                  value={recurringFrequencyValue}
                  onChange={handleFrequencySelect}
                  options={recurringFrequencyOptions.map(option => ({
                    value: option.value,
                    label: option.label
                  }))}
                  placeholder="Seleziona frequenza"
                  className="h-12"
                />
              </div>
            )}

            {/* Riga 2: Modalità + Conto/Persona */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Modalità
                </label>
                  <button
                    type="button"
                    onClick={() => handleDestinationChange(destinationType === 'wallet' ? 'debt' : 'wallet')}
                    className={`w-full h-12 rounded-xl text-sm font-semibold text-white shadow-md transition-all flex items-center justify-center ${destinationType === 'wallet'
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-emerald-600 hover:bg-emerald-700'
                      }`}
                    aria-label="Cambia modalità"
                  >
                    {destinationType === 'wallet' ? 'Conto' : 'Persona'}
                  </button>
              </div>
              <div>
                {destinationType === 'wallet' ? (
                  <>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                      <Wallet className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                      Conto
                    </label>
                    <CustomSelect
                      value={formData.walletId}
                      onChange={(value) => setFormData(prev => ({ ...prev, walletId: value }))}
                      options={[
                        ...(isModelMode ? [{
                          value: '',
                          label: 'Nessuno',
                          icon: <div className="w-4 h-4 rounded-full border-2 border-gray-300 bg-gray-100" />
                        }] : []),
                        ...wallets.map(wallet => ({
                          value: wallet.id,
                          label: wallet.name,
                          icon: (
                            <div
                              className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                              style={{ backgroundColor: wallet.color === 'rainbow' ? '#6366f1' : wallet.color }}
                            />
                          )
                        }))
                      ]}
                      placeholder="Seleziona conto"
                      required={!isModelMode && destinationType === 'wallet'}
                      disabled={destinationType === 'debt'}
                    />
                  </>
                ) : (
                  <>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                      <User className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                      Persona
                    </label>
                    <div className="form-input-group relative">
                      <input
                        ref={personInputRef}
                        type="text"
                        name="debtPersonName"
                        value={formData.debtPersonName}
                        onChange={handleChange}
                        onFocus={handlePersonInputFocus}
                        onBlur={handlePersonInputBlur}
                        placeholder="Inserisci il nome della persona"
                        className="input form-input-with-icon pr-10"
                        autoComplete="off"
                        spellCheck="false"
                      />
                      <User className="form-input-icon" />
                      {showPersonSuggestions && (
                        <div
                          ref={personSuggestionsRef}
                          className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto"
                        >
                          {personSuggestions.length > 0 ? (
                            personSuggestions.map((person, index) => (
                              <button
                                key={index}
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handlePersonSuggestionSelect(person)}
                                className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                              >
                                <User className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                                <span className="truncate">{person}</span>
                              </button>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                              <User className="w-4 h-4" />
                              <span>Nessuna persona trovata</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Riga 3: Negozio (full width) */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Negozio
              </label>
              <div className="form-input-group relative">
                <Store className="form-input-icon" />
                <input
                  ref={storeInputRef}
                  type="text"
                  name="store"
                  value={formData.store}
                  onChange={handleChange}
                  onFocus={handleStoreInputFocus}
                  onBlur={handleStoreInputBlur}
                  onKeyDown={handleStoreInputKeyDown}
                  autoComplete="off"
                  spellCheck="false"
                  className="input form-input-with-icon pr-10"
                  required={false}
                  placeholder="Digita il nome del negozio"
                />
                {/* Suggerimenti negozi */}
                {showStoreSuggestions && (
                  <div
                    ref={storeSuggestionsRef}
                    className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto autocomplete-suggestions"
                  >
                    {storeSuggestions.length > 0 ? (
                      storeSuggestions.map((store, index) => (
                        <button
                          key={index}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleStoreSelect(store);
                          }}
                          onTouchStart={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleStoreSelect(store);
                          }}
                          className="autocomplete-item w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 transition-colors flex items-center gap-2"
                        >
                          <Search className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                          <span className="truncate">{store}</span>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                        <Search className="w-4 h-4" />
                        <span>Nessun negozio trovato</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Riga 4: Data e ora */}
            {!isModelMode && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                      Data
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleDateShift(-1)}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-blue-600 dark:text-blue-400"
                        title="Giorno precedente"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDateShift(1)}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-blue-600 dark:text-blue-400"
                        title="Giorno successivo"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </label>
                  <div>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      className="input"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    Ora
                  </label>
                  <div>
                    <input
                      type="time"
                      name="time"
                      value={formData.time}
                      onChange={handleChange}
                      className="input"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Campo Nome Modello - solo per i modelli, in fondo */}
            {isModelMode && (
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Nome Modello
                </label>
                <div className="form-input-group">
                  <textarea
                    name="note"
                    value={formData.note || ''}
                    onChange={handleChange}
                    placeholder="Inserisci il nome del modello..."
                    rows="3"
                    className="input resize-none"
                    required
                  />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              <div className="flex flex-1 items-center gap-3">
                <button
                  type="button"
                  onClick={() => setIsCalculatorOpen(true)}
                  className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  aria-label="Apri calcolatrice"
                >
                  <Calculator className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="btn btn-secondary flex-1"
                  disabled={isSubmitting}
                >
                  Annulla
                </button>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`btn flex-1 flex items-center justify-center gap-2 ${type === 'expense' ? 'btn-danger' : 'btn-success'} ${isSubmitting ? 'opacity-80' : ''}`}
              >
                {isSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Save className="w-5 h-5" />
                )}
                <span>
                  {isSubmitting ? 'Salvataggio...' : (isModelMode ? 'Salva' : (modelFormData ? 'Salva' : (editingItem ? 'Modifica' : 'Salva')))}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
      <CalculatorModal
        isOpen={isCalculatorOpen}
        onClose={() => setIsCalculatorOpen(false)}
        initialValue={formData.amount ? moneyDigitsToNumber(formData.amount).toFixed(2) : ''}
        onResult={handleCalculatorResult}
      />
      <MobileAmountKeypad
        isOpen={showMobileKeypad && isMobileViewport}
        value={formData.amount}
        onChange={(digits) => setFormData((prev) => ({
          ...prev,
          amount: extractMoneyDigits(digits)
        }))}
        onClose={() => setShowMobileKeypad(false)}
        title="Inserisci importo"
      />
    </>
  );
}

export default ExpenseForm;
